import type {
  FC,
  FocusEvent,
  FocusEventHandler,
  InputHTMLAttributes,
  MouseEvent,
  MouseEventHandler,
  Ref,
} from "react";

import {
  type ComponentEventPropertiesWithKey,
  USER_INPUT_EVENT_CATEGORY,
  addTrackingToEvent,
} from "utils/amplitude";

const NON_TEXT_INPUT_TYPES = [
  "checkbox",
  "radio",
  "file",
  "button",
  "submit",
  "reset",
  "image",
  "range",
  "color",
];

type TrackedInputEventHandler =
  | MouseEventHandler<HTMLInputElement>
  | FocusEventHandler<HTMLInputElement>
  | undefined;

const isTextInputType = (type: string | undefined) => !type || NON_TEXT_INPUT_TYPES.includes(type);

export interface TrackedInputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    ComponentEventPropertiesWithKey {
  ref?: Ref<HTMLInputElement>;
}

/*
 * TrackedInput is a wrapper around the standard HTML input element that adds event tracking capabilities.
 * It accepts all standard input attributes and additional event tracking properties.
 *
 * @param {FormInputProps} props - The properties for the FormInput component.
 * @returns {JSX.Element} The rendered FormInput component.
 */
export const TrackedInput: FC<TrackedInputProps> = ({
  eventName,
  eventProperties: eventProps,
  eventPropertyKey,
  onBlur,
  onClick,
  ...inputProps
}) => {
  let trackingEvent: "onBlur" | "onClick" = "onBlur";
  let onEvent: TrackedInputEventHandler = onBlur;
  let otherProp: { onClick: typeof onClick } | { onBlur: typeof onBlur } = { onClick };

  if (!isTextInputType(inputProps.type)) {
    onEvent = onClick;
    trackingEvent = "onClick";
    otherProp = { onBlur };
  }

  const changeHandler = (event: FocusEvent<HTMLInputElement>) => {
    let eventProperties = eventPropertyKey
      ? { ...eventProps, [eventPropertyKey]: event.target.value }
      : eventProps;

    if (isTextInputType(inputProps.type)) {
      eventProperties = { ...USER_INPUT_EVENT_CATEGORY, ...eventProperties };
    }

    addTrackingToEvent<MouseEvent | FocusEvent>({ eventName, eventProperties, onEvent })(event);
  };

  const props = { ...inputProps, ...otherProp, [trackingEvent]: changeHandler };

  return <input {...props} />;
};

TrackedInput.displayName = "FormInput";
